/**
 * Notice and consent screen decision (k-ID SOW VT-KID-2026-002 + Change Order 1).
 *
 * Inputs:
 *
 *   settings.verification_notice_enabled          workspace-level switch (dcams_settings)
 *   data.notice_and_consent.screen_customization  per-session customization stored at create
 *                                                 time (purpose, retention, displayName,
 *                                                 privacyPolicyUrl, logoUrl). Its presence also
 *                                                 turns the screen on for that session.
 *   data.notice_and_consent.privacy_attestation   the caller's statement that notice and consent
 *                                                 were handled (or determined not required).
 *                                                 get_session returns it as
 *                                                 { present, notice_status, consent_status }.
 *                                                 A present attestation skips the screen.
 *
 * Pure so the rules are unit tested (consentScreen.test.js).
 */

export const CONSENT_PURPOSES = ["age", "identity", "age_and_identity"];
export const CONSENT_RETENTIONS = ["default", "deleted_on_completion"];

const isObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);

/** True when the session carries an attestation that skips the screen. */
export function isPrivacyAttested(data) {
  const att = data?.notice_and_consent?.privacy_attestation;
  if (!isObject(att)) return false;
  // Either the summarized shape from get_session or the stored record itself.
  if (att.present === true) return true;
  return isObject(att.notice) && isObject(att.consent) && !!att.notice.status && !!att.consent.status;
}

/**
 * Resolve what the screen should display. Session values win, then the workspace
 * notice settings, then the defaults the component already applies.
 */
export function resolveConsentScreen({ settings, data } = {}) {
  const nac = data?.notice_and_consent;
  const cfg = isObject(nac?.screen_customization) ? nac.screen_customization : null;
  const purpose = CONSENT_PURPOSES.includes(cfg?.purpose) ? cfg.purpose : "age_and_identity";
  const retention = CONSENT_RETENTIONS.includes(cfg?.retention) ? cfg.retention : "default";

  const workspaceEnabled = settings?.verification_notice_enabled === true;
  const attested = isPrivacyAttested(data);
  const enabled = (workspaceEnabled || !!cfg) && !attested;

  return {
    enabled,
    attested,
    purpose,
    retention,
    customerName: cfg?.displayName || settings?.verification_notice_customer_name || undefined,
    customerPrivacyPolicyUrl: cfg?.privacyPolicyUrl || settings?.verification_notice_customer_privacy_url || undefined,
    customerLogoUrl: cfg?.logoUrl || data?.styles?.logo_url || data?.logo_url || undefined,
  };
}

/**
 * Pick the locale key for a purpose variant. The default copy keeps its existing keys;
 * the age and identity variants live beside them with a suffix, and fall back to the
 * default key when a language has not been translated yet.
 */
export function variantText(section, key, purpose) {
  if (!section) return undefined;
  if (purpose && purpose !== "age_and_identity") {
    const v = section[`${key}_${purpose}`];
    if (v) return v;
  }
  return section[key];
}

/** Retention sentence key for the Read more disclosure. */
export function retentionText(section, retention) {
  if (!section) return undefined;
  if (retention === "deleted_on_completion" && section.text_deleted_on_completion) {
    return section.text_deleted_on_completion;
  }
  return section.text;
}
